import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaGoogle, FaLinkedin } from "react-icons/fa";
import { ChevronDown, ShieldCheck } from "lucide-react";
import * as z from "zod";
import { toast } from "sonner"
import { setCookie, getCookie } from '@/lib/cookies';
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Profile } from '@/types';

type Props = {
    code: string;
    schema: z.ZodObject;
    children: React.ReactNode;
    title?: string;
    description?: string;
    buttonName?: string;
    onSubmitted?: (data: { [key: string]: any }) => void;
    initialValues?: { [key: string]: any };
};

export const Form: React.FC<Props> = ({ code, schema, children, title, description, buttonName = "Submit", onSubmitted, initialValues = {} }) => {
    type T = z.infer<typeof schema>;

    const [provider, setProvider] = useState<'google' | 'linkedin'>();
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const formFillCookie = getCookie('form_fill') as 'google' | 'linkedin' | null;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [values, setValues] = useState(initialValues);
    const form = useForm<T>({
        resolver: zodResolver(schema),
        mode: "onChange",
        values: values,
    });

    useEffect(() => {
        if (formFillCookie) {
            onFill(formFillCookie);
            setCookie('form_fill', '', -1);
        }
    }, [formFillCookie]);

    const onSubmit = async (data: T) => {
        setIsSubmitting(true);
        const fetchPromise: Promise<T> = new Promise(async (resolve, reject) => {
            fetch('/form/submission', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, data }),
            }).then((response) => {                
                if (!response.ok) {
                    reject(new Error('Form submission failed'));
                    return;
                }

                setTimeout(async () => {
                    resolve(data);
                    onSubmitted?.({ ...data, ...await response.json() });
                    form.reset();
                }, 3000);
            }).catch(error => {
                reject(error)
            }).finally(() => {
                setTimeout(() => setIsSubmitting(false), 3000);
            });
        })

        toast.promise(fetchPromise, {
            loading: 'Submitting...',
            success: 'Submitted successfully',
            error: (e: Error) => e?.message || 'Unable to submit changes',
        })

        return true;
    }

    const onFill = async (provider: 'google' | 'linkedin') => {
        setProvider(provider);
        await fetch(`/form/fill/${provider}`, {
            method: 'GET'
        }).then((response) => response.json()).then((data) => {
            if (data?.redirect) {
                setCookie('form_fill', provider, 1);
                setCookie('redirect_url', window.location.href, 1);
                window.location.href = data.redirect;

                return;
            }

            setProfiles(data || []);
            setDialogOpen(true);
        })
    }

    return (
        <FormProvider {...form}>
            <form name={code} onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {title && <h1 className="text-3xl font-bold">{title}</h1>}
                {description && <p className="pb-4">{description}</p>}
                {children}
                <div className="flex justify-center pt-4">
                    <ButtonGroup>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="lg" className="py-4 px-6 border-blue-300 hover:bg-blue-50 text-primary">
                                    Fill form with<Separator className="bg-white ml-2" orientation="vertical" /><ChevronDown className="ml-2" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => { onFill("google") }}>
                                    <FaGoogle className="mr-1 text-red-500" /> Google
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { onFill("linkedin") }}>
                                    <FaLinkedin className="mr-1 text-blue-500" /> LinkedIn
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Button size="lg" type="submit" disabled={!form.formState.isValid || isSubmitting} className="py-4 px-6">
                            {isSubmitting ? 'Submitting...' : buttonName}
                        </Button>
                    </ButtonGroup>
                </div>
                <div className="text-sm text-gray-500 mt-4 text-center">
                    By submitting this form, you agree to our <Link href="/legal/privacy-policy" className="text-primary underline hover:no-underline inline-flex items-center">
                        <ShieldCheck size={16} className="mr-1" /> Privacy Policy
                    </Link>.
                </div>
            </form>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            <div className="flex items-center">
                                Select a {provider === 'google' ? (<FaGoogle size="21" className="mx-2 text-red-500" />) : provider === 'linkedin' ? (<FaLinkedin size="21" className="mx-2 text-blue-500" />) : null} profile
                            </div>
                        </DialogTitle>
                        <DialogDescription>Choose a profile to fill the form.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-4">
                        {profiles.map((profile, index) => (
                            <div
                                key={index}
                                className="p-2 rounded-lg hover:shadow-sm transition-shadow border border-gray-300 bg-white h-full cursor-pointer"
                                onClick={() => {
                                    form.setValue("firstName", profile.firstName);
                                    form.setValue("lastName", profile.lastName);
                                    form.setValue("email", profile.email);
                                    form.setValue("company", profile.company);
                                    form.trigger();
                                    setDialogOpen(false);
                                }}
                            >
                                <div className="bg-slate-100 p-4 rounded-md text-gray-900 h-full">
                                    <p className="font-bold">{profile.firstName} {profile.lastName}</p>
                                    <p className="text-sm text-gray-600">{profile.email}</p>
                                    {profile.company && <p className="text-sm text-gray-600">{profile.company}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (provider) {
                                    setCookie('form_fill', provider, 1);
                                    setCookie('redirect_url', window.location.href, 1);
                                    window.location.href = `/social/authorize/${provider}`;
                                }
                            }}
                        >
                            <div className="flex items-center">
                                {provider === 'google' ? (<FaGoogle className="mr-2 text-red-500" />) : provider === 'linkedin' ? (<FaLinkedin className="mr-2 text-blue-500" />) : null}
                                <span>Add New</span>
                            </div>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </FormProvider>
    )
}

export default Form;
export * from "./inputs";
